import { Button, Flex, Tabs as TabBar } from "../../components";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { MenuCategory } from "../../types/item";
import { useHorizontalScroll } from "../../hooks";
import { useEffect, useRef, useState } from "react";

const categoryId = (uuid?: string) => `${uuid}_category`;

const Tabs = ({ menu }: { menu: MenuCategory[] }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [activeUuid, setActiveUuid] = useState(menu[0]?.uuid);
  const { ref: scrollerRef, needsScroll, canScrollLeft, canScrollRight, scroll } =
    useHorizontalScroll(menu);

  const lockedUuid = useRef<string | null>(null);
  const scrollStopTimer = useRef<number>();

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const syncHeight = () => {
      document.documentElement.style.setProperty(
        "--menu-tabs-height",
        `${bar.offsetHeight}px`,
      );
    };
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const syncActive = () => {
      if (lockedUuid.current) return;
      const probe = (barRef.current?.getBoundingClientRect().bottom ?? 80) + 2;
      let current = menu[0]?.uuid;
      for (const category of menu) {
        const el = document.getElementById(categoryId(category.uuid));
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= probe && rect.bottom > probe) {
          current = category.uuid;
          break;
        }
        if (rect.top <= probe) current = category.uuid;
      }
      if (current) setActiveUuid(current);
    };

    const onScroll = () => {
      window.clearTimeout(scrollStopTimer.current);
      if (lockedUuid.current) {
        scrollStopTimer.current = window.setTimeout(() => {
          lockedUuid.current = null;
          syncActive();
        }, 150);
        return;
      }
      syncActive();
    };

    syncActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(scrollStopTimer.current);
    };
  }, [menu]);

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root || !activeUuid) return;
    const tab = root.querySelector(`[data-tab="${activeUuid}"]`);
    tab?.scrollIntoView({
      inline: "center",
      block: "nearest",
      behavior: "smooth",
    });
  }, [activeUuid]);

  return (
    <div
      ref={barRef}
      className={`sticky top-0 left-0 right-0 z-[100] bg-background`}
    >
      <div
        className={needsScroll ? "px-[1.2rem]" : "px-page"}
      >
        <Flex align="center" wrap="nowrap" gap={"1.2rem"} className="py-[30px]">
          {needsScroll && (
            <Button
              outlined
              color="secondary"
              icon={<LeftOutlined />}
              minWidth="3.2rem"
              aria-label="Scroll categories left"
              aria-disabled={!canScrollLeft}
              className={`shrink-0 !h-[3.2rem] !rounded-full !px-[0.8rem] ${
                canScrollLeft ? "" : "pointer-events-none opacity-40"
              }`}
              onClick={() => canScrollLeft && scroll(-1)}
            />
          )}
          <TabBar
            variant="pill"
            scrollerRef={scrollerRef}
            centered={!needsScroll}
            value={activeUuid}
            items={menu.map((category, index) => ({
              value: category.uuid || `category-${index}`,
              label: category.name,
            }))}
            onChange={(uuid) => {
              if (!menu.some((category) => category.uuid === uuid)) return;
              lockedUuid.current = uuid;
              setActiveUuid(uuid);
              document
                .getElementById(categoryId(uuid))
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          />
          {needsScroll && (
            <Button
              outlined
              color="secondary"
              icon={<RightOutlined />}
              minWidth="3.2rem"
              aria-label="Scroll categories right"
              aria-disabled={!canScrollRight}
              className={`shrink-0 !h-[3.2rem] !rounded-full !px-[0.8rem] ${
                canScrollRight ? "" : "pointer-events-none opacity-40"
              }`}
              onClick={() => canScrollRight && scroll(1)}
            />
          )}
        </Flex>
      </div>
    </div>
  );
};

export default Tabs;
